// app/services/automation/page.tsx
"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Bolt,
  Schedule,
  CheckCircle,
  Psychology,
  IntegrationInstructions,
  Build,
  AutoFixHigh,
  Support,
  AccessTime,
  ErrorOutline,
  TrendingUp,
  Groups,
} from "@mui/icons-material";
import PopoverForm from "@/components/PopoverForm";

export default function Automation() {
  const theme = useTheme();
  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  const whyAutomatePoints = [
    {
      icon: <AccessTime sx={{ fontSize: 40 }} />,
      title: "Time Savings",
      description:
        "Cut hours of manual work every week and focus on high-value tasks.",
    },
    {
      icon: <ErrorOutline sx={{ fontSize: 40 }} />,
      title: "Error Reduction",
      description:
        "Minimize mistakes and improve consistency across all processes.",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Scalability",
      description:
        "Workflows that grow with your business and adapt to changing needs.",
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "Expert Integration",
      description:
        "20+ years of combined IT and development knowledge in every solution.",
    },
  ];

  const automationAreas = [
    {
      category: "CRM & Sales",
      items: [
        "Lead Management",
        "Follow-up Automation",
        "Sales Reporting",
        "Pipeline Updates",
      ],
      icon: "📊",
    },
    {
      category: "Marketing",
      items: [
        "Email Campaigns",
        "Social Scheduling",
        "Analytics Integration",
        "Lead Scoring",
      ],
      icon: "🎯",
    },
    {
      category: "Finance & Accounting",
      items: [
        "Automated Invoicing",
        "Payment Reconciliation",
        "Expense Tracking",
        "Financial Reports",
      ],
      icon: "💰",
    },
    {
      category: "Operations",
      items: [
        "Task Assignments",
        "Notifications",
        "Approval Workflows",
        "Resource Management",
      ],
      icon: "⚙️",
    },
    {
      category: "AI Integrations",
      items: [
        "Chatbots",
        "Smart Assistants",
        "Data Insights",
        "Predictive Analytics",
      ],
      icon: "🤖",
    },
  ];

  const processSteps = [
    {
      label: "Workflow Discovery",
      description:
        "We map your processes and identify automation opportunities for maximum impact.",
      icon: <Psychology />,
    },
    {
      label: "Tool Selection",
      description:
        "Choose the right platforms: Make.com, Zapier, APIs, or custom scripts.",
      icon: <IntegrationInstructions />,
    },
    {
      label: "Integration & Development",
      description:
        "Connecting apps and systems with robust, reliable automation.",
      icon: <Build />,
    },
    {
      label: "Testing & Optimization",
      description:
        "Ensure reliability, efficiency, and seamless user experience.",
      icon: <AutoFixHigh />,
    },
    {
      label: "Scaling & Support",
      description:
        "Continuous improvements, monitoring, and performance upgrades.",
      icon: <Support />,
    },
  ];

  const techStack = {
    "Automation Platforms": ["Make.com", "Zapier", "n8n", "Power Automate"],
    "APIs & Integrations": ["REST APIs", "GraphQL", "Webhooks", "Custom APIs"],
    "AI Tools": ["ChatGPT", "Custom ML Models", "NLP", "Computer Vision"],
    "Cloud Services": ["AWS", "Google Cloud", "Firebase", "Azure"],
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "white" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                AI-Powered Automation for Smarter, Faster Business
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                We help you eliminate repetitive tasks, connect systems, and
                scale operations with intelligent automation.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mt: 4, flexWrap: "wrap" }}>
                <PopoverForm
                  triggerElement={
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Bolt />}
                      sx={{
                        bgcolor: "white",
                        color: "primary.main",
                        px: 4,
                        "&:hover": {
                          bgcolor: alpha("#fff", 0.9),
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => window.open("/contact", "_self")}
                    >
                      Automate My Business
                    </Button>
                  }
                />

                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<Schedule />}
                  onClick={handleConsult}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: alpha("#fff", 0.1),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Book a Discovery Call
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  background: alpha("#fff", 0.1),
                  borderRadius: 4,
                  p: 4,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  ⚡ Quick Impact
                </Typography>
                <List>
                  {[
                    "Reduce manual work by 80%+",
                    "Implement in 2-4 weeks",
                    "Scale as you grow",
                    "24/7 monitoring & support",
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: "white" }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Automate with LinXar Labs Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Why Automate with LinXar Labs
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            Transform your business operations with intelligent automation
            solutions
          </Typography>

          <Grid container spacing={4}>
            {whyAutomatePoints.map((point, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Box
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 3,
                      }}
                    >
                      {point.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {point.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {point.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What We Automate Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            What We Automate
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            Comprehensive automation solutions across all business functions
          </Typography>

          <Grid container spacing={4}>
            {automationAreas.map((area, index) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 3,
                        gap: 2,
                      }}
                    >
                      <Typography variant="h3" sx={{ fontSize: "2.5rem" }}>
                        {area.icon}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {area.category}
                      </Typography>
                    </Box>
                    <Box>
                      {area.items.map((item, itemIndex) => (
                        <Typography
                          key={itemIndex}
                          variant="body2"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1.5,
                            color: "text.primary",
                            fontWeight: 500,
                            "&:before": {
                              content: '"▸"',
                              color: theme.palette.primary.main,
                              fontWeight: "bold",
                              mr: 1.5,
                            },
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Automation Process Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundImage: `url('/auto.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: alpha(theme.palette.background.paper, 0.77),
            zIndex: 0,
          }}
        />
        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Our Automation Process
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            A systematic approach to delivering reliable, scalable automation
            solutions
          </Typography>

          <Stepper orientation="vertical" sx={{ maxWidth: 800, mx: "auto" }}>
            {processSteps.map((step) => (
              <Step key={step.label} active={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1, lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Tech Stack Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Technology Stack & Tools
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            We leverage industry-leading platforms and custom solutions for
            maximum efficiency
          </Typography>

          <Grid container spacing={4}>
            {Object.entries(techStack).map(([category, technologies]) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={category}>
                <Card
                  sx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: "white",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {category}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {technologies.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Study Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                SaaS Onboarding Automation
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Client: SaaS Startup in the Netherlands
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  Challenge
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Manual onboarding process wasting 10+ hours per week, leading
                  to delays and inconsistent client experience.
                </Typography>

                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  Solution
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Automated onboarding pipeline with Make.com + CRM integration,
                  including welcome sequences, account setup, and training
                  scheduling.
                </Typography>
              </Box>

              <Box
                sx={{
                  background: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 700, color: theme.palette.success.main }}
                >
                  Results
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      85%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Faster Onboarding
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      +40%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Client Satisfaction
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: "white",
                  p: 4,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  Ready to Automate Your Processes?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                  Let&apos;s identify automation opportunities and transform
                  your business efficiency.
                </Typography>
                <PopoverForm
                  triggerElement={
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: "white",
                        color: theme.palette.primary.main,
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: alpha("#fff", 0.9),
                        },
                      }}
                    >
                      Start Automation Audit
                    </Button>
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          py: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 3,
            }}
          >
            Automate Your Business Today
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 6, lineHeight: 1.6 }}
          >
            Stop wasting time on repetitive work — let automation take over and
            focus on what matters.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <PopoverForm
              triggerElement={
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Bolt />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Automate My Business
                </Button>
              }
            />
            <Button
              variant="outlined"
              size="large"
              onClick={handleConsult}
              startIcon={<Schedule />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Book a Discovery Call
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
